/**
 * DraggableCorridor — Interactive price corridor with drag-to-adjust.
 *
 * Visual match of PriceCorridor.tsx but with pointer-event drag on the dot.
 * Snaps to $5 increments. 44px hit target for WCAG compliance.
 */

import { useRef, useState, useCallback, useMemo } from 'react';
import { T } from '@/styles/tokens';

const NAVY = '#1B2A4A';

export interface DraggableCorridorProps {
  floorCents: number;
  priceCents: number;
  onPriceChange: (cents: number) => void;
  medium?: string;
  width?: number;
  height?: number;
}

function toDollars(cents: number): number {
  return Math.round(cents / 100);
}

function getMarkerColor(pricePct: number, floorPct: number): string {
  if (pricePct < floorPct) return '#EF4444';
  if (pricePct > 65) return '#AF510E';
  return T.primary;
}

export function DraggableCorridor({
  floorCents, priceCents, onPriceChange, medium, width, height,
}: DraggableCorridorProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const priceDollars = toDollars(priceCents);
  const floorDollars = toDollars(floorCents);

  const { floorPct, pricePct, rangeMin, rangeMax, markerColor } = useMemo(() => {
    const estimatedCeiling = floorDollars * 2.5;
    const rMin = priceDollars < floorDollars ? priceDollars * 0.85 : floorDollars * 0.85;
    const rMax = Math.max(estimatedCeiling, priceDollars * 1.3);
    const span = rMax - rMin;
    const fp = ((floorDollars - rMin) / span) * 100;
    const pp = ((priceDollars - rMin) / span) * 100;
    return {
      floorPct: fp,
      pricePct: pp,
      rangeMin: rMin,
      rangeMax: rMax,
      markerColor: getMarkerColor(pp, fp),
    };
  }, [priceDollars, floorDollars]);

  // Pointer → price conversion
  const pointerToPrice = useCallback((clientX: number) => {
    const bar = barRef.current;
    if (!bar) return priceCents;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const span = rangeMax - rangeMin;
    const dollars = rangeMin + pct * span;
    // Snap to $5 (500 cents)
    const snapped = Math.round(dollars / 5) * 5;
    return Math.max(0, snapped * 100);
  }, [priceCents, rangeMin, rangeMax]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setIsDragging(true);
    onPriceChange(pointerToPrice(e.clientX));
  }, [onPriceChange, pointerToPrice]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    onPriceChange(pointerToPrice(e.clientX));
  }, [isDragging, onPriceChange, pointerToPrice]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const transition = isDragging ? 'none' : 'left 240ms cubic-bezier(0.32,0.72,0,1)';
  const colorTransition = 'background-color 200ms ease';

  return (
    <div style={{ touchAction: 'none', userSelect: 'none' }}>
      {/* Flag tag */}
      <div style={{ position: 'relative', height: 38, marginBottom: 0 }}>
        <div style={{
          position: 'absolute',
          left: `${pricePct}%`,
          transform: 'translateX(-8px)',
          display: 'flex', flexDirection: 'column' as const, alignItems: 'flex-start',
          transition,
        }}>
          <div style={{
            position: 'relative',
            height: 28, padding: '0 10px 0 22px',
            borderRadius: '14px 4px 4px 14px',
            background: markerColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: 13, fontWeight: 700,
            boxShadow: `0 2px 8px ${markerColor}30, 0 1px 4px rgba(0,0,0,0.10)`,
            whiteSpace: 'nowrap', letterSpacing: -0.3,
            fontVariantNumeric: 'tabular-nums',
            width: priceDollars >= 10000 ? 80 : priceDollars >= 1000 ? 70 : 60,
            transition: colorTransition,
            fontFamily: T.fontFamily,
          }}>
            <div style={{
              position: 'absolute', left: 5, top: '50%', transform: 'translateY(-50%)',
              width: 6, height: 6, borderRadius: '50%',
              background: T.cream, boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.12)',
            }} />
            ${priceDollars.toLocaleString()}
          </div>
          <div style={{
            width: 2, height: 8, backgroundColor: markerColor,
            marginLeft: 8, transition: colorTransition,
          }} />
        </div>
      </div>

      {/* Bar */}
      <div
        ref={barRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: 'relative', height: 14,
          borderRadius: '7px 0 0 7px',
          backgroundColor: 'transparent',
          overflow: 'visible',
          cursor: 'pointer',
        }}
      >
        {/* Danger zone */}
        {floorPct > 0 && (
          <div style={{
            position: 'absolute', left: 0, top: 0,
            width: `${floorPct}%`, height: '100%',
            borderRadius: '7px 0 0 7px',
            background: 'linear-gradient(to right, rgba(239,68,68,0.22) 0%, rgba(239,68,68,0.08) 100%)',
            transition,
          }} />
        )}
        {/* Safe zone */}
        <div style={{
          position: 'absolute', left: `${floorPct}%`, top: 0,
          width: `${100 - floorPct}%`, height: '100%',
          background: 'linear-gradient(to right, rgba(61,171,165,0.08) 0%, rgba(61,171,165,0.28) 6%, rgba(61,171,165,0.28) 30%, rgba(61,171,165,0.24) 50%, rgba(61,171,165,0.18) 65%, rgba(61,171,165,0.08) 82%, transparent 100%)',
          transition,
        }} />
        {/* Floor line */}
        <div style={{
          position: 'absolute', left: `${floorPct}%`,
          top: -4, bottom: -4, width: 2.5,
          backgroundColor: T.text, transform: 'translateX(-50%)',
          transition,
        }} />
        {/* Draggable dot — 44px hit target */}
        <div
          style={{
            position: 'absolute',
            left: `${pricePct}%`, top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 44, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10,
            cursor: isDragging ? 'grabbing' : 'grab',
            transition,
          }}
        >
          <div style={{
            width: isDragging ? 18 : 14,
            height: isDragging ? 18 : 14,
            borderRadius: '50%',
            backgroundColor: markerColor,
            border: '2.5px solid white',
            boxShadow: isDragging
              ? `0 0 0 4px ${markerColor}20, 0 2px 8px ${markerColor}44`
              : `0 0 0 1px ${markerColor}22, 0 2px 6px ${markerColor}33`,
            transition: isDragging ? colorTransition : `all 150ms ease, ${colorTransition}`,
          }} />
        </div>
      </div>

      {/* Axis labels */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        marginTop: 12, alignItems: 'flex-start',
      }}>
        <div>
          <span style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase' as const,
            letterSpacing: 0.8, color: '#7A7670', display: 'block', marginBottom: 2,
            fontFamily: T.fontFamily,
          }}>Your Minimum</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: NAVY, fontFamily: T.fontFamily }}>
            ${floorDollars.toLocaleString()}
          </span>
        </div>
        <span style={{ fontSize: 11, color: T.charcoal400, fontStyle: 'italic', fontFamily: T.fontFamily }}>
          market thins out →
        </span>
      </div>
    </div>
  );
}
